import { createContext, useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { isRootPath } from '../nav'
import { getWebApp, hasTelegramBack } from '../telegram'
import { backStack } from './backStack'
import { useViewBackDecision, useViewCanGoBack } from './navigation'
import { parentView } from './navigationMemory'
import { dismissKeyboard } from './keyboard'

/** Назад по истории; если страницу открыли напрямую, в её раздел (группа — в «Избранное», инструмент — в «Инструменты»), иначе на главную. */
export function useBack(fallback?: string): () => void {
  const location = useLocation()
  const navigate = useNavigate()
  const decision = useViewBackDecision()
  return useCallback(() => {
    const action = decision()
    if (action !== 'pending') dismissKeyboard()
    if (action === 'back') void navigate(-1)
    else if (action === 'fallback') void navigate(fallback ?? parentView(location.pathname), { replace: true })
  }, [decision, fallback, location.pathname, navigate])
}

/** Слой, в котором рисуется компонент: 0 — сам экран, иначе id карточки или окна поверх. */
export const BackLayer = createContext(0)

/**
 * Встать в стек «Назад», пока компонент на экране и `active`: «Назад» Telegram
 * и Esc закроют верхний слой. Обработчик можно менять на каждом рендере — место
 * в стеке не меняется. Возвращает id слоя (для `BackLayer` и `isTopLayer`).
 */
export function useBackHandler(handler: () => void, active = true): number {
  const [id] = useState(() => backStack.nextId())
  const ref = useRef(handler)
  useEffect(() => {
    ref.current = handler
  })
  useEffect(() => {
    if (!active) return
    backStack.push(id, () => { dismissKeyboard(); ref.current() })
    return () => backStack.remove(id)
  }, [id, active])
  return id
}

/** Слой сверху: клавиши карточки молчат, пока поверх неё окно или лист. */
export function isTopLayer(layer: number): boolean {
  return backStack.isTop(layer)
}

/** Сколько слоёв открыто поверх экрана. */
export function useBackDepth(): number {
  return useSyncExternalStore(backStack.subscribe, backStack.size)
}

// Esc закрывает верхний слой — на компьютере и в Telegram Desktop. Ловим раньше всех,
// чтобы Esc в листе над карточкой не закрыл и карточку.
if (typeof window !== 'undefined') {
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return
      if (backStack.back()) {
        e.preventDefault()
        e.stopPropagation()
      }
    },
    true,
  )
}

/**
 * Системная «Назад» Telegram (6.1+). Видна на вложенных экранах и пока поверх
 * что-то открыто (даже на корневом экране); закрывает верхний слой, а когда
 * слоёв нет — уводит назад по разделам. Ставится один раз, в оболочке.
 */
export function useTelegramBackButton(): void {
  const { pathname } = useLocation()
  const back = useBack()
  const hasPrevious = useViewCanGoBack()
  const nested = !isRootPath(pathname) || hasPrevious
  const depth = useBackDepth()
  const routeBack = useRef({ back, nested })
  useEffect(() => {
    routeBack.current = { back, nested }
  })

  useEffect(() => {
    const button = getWebApp()?.BackButton
    if (!button || !hasTelegramBack()) return
    const onClick = () => {
      if (backStack.back()) return
      if (routeBack.current.nested) routeBack.current.back()
    }
    button.onClick(onClick)
    return () => {
      button.offClick(onClick)
      button.hide()
    }
  }, [])

  useEffect(() => {
    const button = getWebApp()?.BackButton
    if (!button || !hasTelegramBack()) return
    if (depth > 0 || nested) button.show()
    else button.hide()
  }, [depth, nested])
}
